/**
 * decide.js —— 一页纸决策 · 全站脚本第一段（主题 + 场景引擎 + 公共小件）
 * ---------------------------------------------------------------------------
 * ① 主题切换（页头 #dz-theme；键 tb-theme 与工具站/台账共享，一处切换全站生效）
 * ② 返回顶部（滚动超一屏出现）
 * ③ 场景引擎（[data-dz-scene]）：按 data js 渲染问卷 → compute 判分 → 渲染行动清单 → 打印
 *    场景数据注册在 window.DZ_SCENES（static/decide/data/<slug>.js，仅场景页加载）
 * 公共小件挂在 window.DZ_UI（第二段 decide-ai.js 复用）。
 *
 * 纪律：结果区全 createElement/textContent（天然防注入）；答案只存本机 localStorage；
 * 同状态零 DOM 写入（观察者/滚动回调铁律）。
 */
(function (global) {
  'use strict';

  var doc = global.document;
  var SVG_NS = 'http://www.w3.org/2000/svg';

  function el(tag, cls) {
    var node = doc.createElement(tag);
    if (cls) { node.className = cls; }
    return node;
  }
  function span(cls, text) {
    var node = el('span', cls);
    node.textContent = text;
    return node;
  }
  function iconEl(name) {
    var svg = doc.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'tool-icon');
    svg.setAttribute('aria-hidden', 'true');
    var use = doc.createElementNS(SVG_NS, 'use');
    use.setAttribute('href', '#i-' + name);
    svg.appendChild(use);
    return svg;
  }
  function toast(msg) {
    var UI = global.ToolUI || {};
    if (UI.toast) { UI.toast(msg); }
  }

  /* 公共小件出口（decide-ai.js 复用，避免两份拷贝漂移） */
  global.DZ_UI = { el: el, span: span, icon: iconEl, toast: toast };

  /* ---------------- ① 主题 ---------------- */
  function initTheme() {
    var btn = doc.getElementById('dz-theme');
    if (!btn) { return; }
    function label() {
      var dark = doc.documentElement.getAttribute('data-theme') === 'dark';
      btn.setAttribute('aria-label', dark ? '切换到浅色主题' : '切换到深色主题');
      btn.setAttribute('aria-pressed', dark ? 'true' : 'false');
    }
    label();
    btn.addEventListener('click', function () {
      var dark = doc.documentElement.getAttribute('data-theme') === 'dark';
      var next = dark ? 'light' : 'dark';
      doc.documentElement.setAttribute('data-theme', next);
      try { global.localStorage.setItem('tb-theme', next); } catch (e) { /* 隐私模式不持久化 */ }
      label();
    });
  }

  /* ---------------- ② 返回顶部（对齐 tools/CMS：悬浮按钮 + 滚动进度环） ---------------- */
  function initBackTop() {
    var wrap = el('div', 'tool-float');
    var btn = el('button', 'tool-float-btn');
    btn.type = 'button';
    btn.setAttribute('aria-label', '回到顶部');
    btn.setAttribute('title', '回到顶部');
    var ring = doc.createElementNS(SVG_NS, 'svg');
    ring.setAttribute('class', 'tool-float-ring');
    ring.setAttribute('viewBox', '0 0 48 48');
    ring.setAttribute('aria-hidden', 'true');
    var ringCls = ['tool-float-ring-bg', 'tool-float-ring-pg'];
    var pg = null;
    for (var i = 0; i < ringCls.length; i++) {
      var c = doc.createElementNS(SVG_NS, 'circle');
      c.setAttribute('class', ringCls[i]);
      c.setAttribute('cx', '24');
      c.setAttribute('cy', '24');
      c.setAttribute('r', '22');
      if (i === 1) { pg = c; }
      ring.appendChild(c);
    }
    btn.appendChild(ring);
    btn.appendChild(iconEl('arrow-up'));
    wrap.appendChild(btn);
    doc.body.appendChild(wrap);
    btn.addEventListener('click', function () {
      global.scrollTo({ top: 0, behavior: 'smooth' });
    });
    var threshold = 300;
    var CIRC = 138.2; // 2π×22，进度环周长（与 tools.css stroke-dasharray 一致）
    var lastPct = -1;
    function sync() {
      var top = global.pageYOffset || doc.documentElement.scrollTop || 0;
      var show = top > threshold;
      var max = doc.documentElement.scrollHeight - global.innerHeight;
      var pct = max > 0 ? Math.min(Math.max(top / max, 0), 1) : 0;
      var pctChanged = pct !== lastPct;
      if (pctChanged) { lastPct = pct; }
      btn.className = btn.className.replace(/\s*is-show/, '') + (show ? ' is-show' : '');
      if (pctChanged) { pg.style.strokeDashoffset = (CIRC * (1 - pct)).toFixed(1); }
    }
    global.addEventListener('scroll', sync, { passive: true });
    sync();
  }

  /* ---------------- ③ 场景引擎 ---------------- */
  function todayISO() {
    var d = new Date();
    var m = d.getMonth() + 1;
    var dd = d.getDate();
    return d.getFullYear() + '-' + (m < 10 ? '0' + m : m) + '-' + (dd < 10 ? '0' + dd : dd);
  }

  /* 三态解读工厂（2026-09-24 收敛：五个平衡单 compute 的同构三元链收进引擎）——
     |diff| ≤ threshold 视为两边接近（态①），其余按 diff 正负取行动态/默认态文案（态②③）。
     文案是模板串，{n} = |diff|（可出现多次）；threshold 可配，缺省 8（存量五单统一口径，
     改「接近」的容忍带只动这一处）。产品红线在此锁定：只陈述分差与下一步，高分也不替用户拍板。
     Node 侧（冒烟 loadCfg）与本浏览器共用同一份实现：sandbox 里执行本文件即得 DZ_NOTE3
     （文件尾 boot 对无 document 环境防御，见文件尾注释）。 */
  function note3(diff, tpl, threshold) {
    var t = tpl || {};
    var n = String(Math.abs(diff));
    var pick = Math.abs(diff) <= (threshold == null ? 8 : threshold)
      ? t.close
      : (diff > 0 ? t.aHigh : t.bHigh);
    return String(pick == null ? '' : pick).split('{n}').join(n);
  }
  global.DZ_NOTE3 = note3;

  function mountScene(root) {
    var slug = String(root.getAttribute('data-dz-scene') || '');
    var cfg = (global.DZ_SCENES || {})[slug];
    var quizBox = root.querySelector('[data-dz-quiz]');
    var factsBox = root.querySelector('[data-dz-facts]');
    var errBox  = root.querySelector('[data-dz-error]');
    var genBtn  = root.querySelector('[data-dz-generate]');
    var resultBox = doc.querySelector('[data-dz-result]');
    if (!cfg || !quizBox || !genBtn) { return; }

    var storeKey = 'dz-answers-' + slug;
    /* 步进问卷（cfg.stepper）的答案状态：单题替换渲染下未展示的题不在 DOM，
       答案统一收在 picked，生成/持久化/AI 快照都从它读（平铺模式仍走 DOM，为 null） */
    var picked = cfg.stepper ? {} : null;
    var stepperGo = null;
    /* AI 上下文双通道（2026-09-24 结果↔AI 结合）：
       DZ_LAST = 生成后的判分快照（decide-ai 优先读，带 dims/diff）；
       DZ_DRAFT = 点选/输入的实时草稿——用户没生成结果就先开聊时兜底
       （步进 DOM 只有当前题，控件扫描抓不到已答题）。labels 题名映射共用 */
    var labels = {};
    for (var n = 0; n < cfg.quiz.length; n++) { labels[cfg.quiz[n].name] = cfg.quiz[n].label; }
    if (cfg.facts) {
      for (var n2 = 0; n2 < cfg.facts.length; n2++) { labels[cfg.facts[n2].name] = cfg.facts[n2].label; }
    }
    /* 结果标签（cfg.resultUI 可配，#127 兴趣班起）：两难两侧的名称——三统计卡、
       明细表头、纸面 kv 消费。缺省 = 老版口径「留下/离开/走−留」，#128 不配置零变化 */
    var ui = cfg.resultUI || {};
    var stayName = ui.stayName || '留下';   // 默认态长名（不报/不走的一侧）
    var goName = ui.goName || '离开';       // 行动态长名
    var stayShort = ui.stayShort || '留';   // 纸面 kv 短名
    var goShort = ui.goShort || '走';
    global.DZ_DRAFT = { slug: slug, answers: {}, labels: labels };
    function syncDraft(name, val) {
      if (global.DZ_DRAFT && global.DZ_DRAFT.slug === slug) {
        global.DZ_DRAFT.answers[name] = String(val == null ? '' : val).trim();
      }
    }
    /* 行动清单勾选记忆（承老版 checkedActs，2026-09-23 迁移）：按行动文本作键，
       回改答案重算后同文本的行动恢复勾选；重填不清（成立的行动仍然成立） */
    var checkedActs = {};

    function showError(msg) {
      if (!errBox) { return; }
      if (msg) {
        errBox.textContent = msg;
        errBox.hidden = false;
      } else if (!errBox.hidden) {
        errBox.hidden = true;
        errBox.textContent = '';
      }
    }

    /* 表单字段（quiz 平铺 / facts 表单共用；id + label[for]：AI 上下文收集器按同款口径抓） */
    function fieldOf(q) {
      var wrap = el('label', q.type === 'textarea' ? 'dz-field dz-field--full' : 'dz-field');
      wrap.setAttribute('for', 'dz-' + q.name);
      var name = el('span', 'dz-label');
      name.appendChild(doc.createTextNode(q.label));
      if (q.optional) {
        var op = el('span', 'dz-label-opt');
        op.textContent = '选填';
        name.appendChild(op);
      }
      wrap.appendChild(name);
      if (q.hint) {
        var hint = el('span', 'dz-hint');
        hint.textContent = q.hint;
        wrap.appendChild(hint);
      }
      if (q.type === 'textarea') {
        var ta = doc.createElement('textarea');
        ta.id = 'dz-' + q.name;
        ta.className = 'tool-input dz-input';
        ta.rows = 3;
        ta.maxLength = 200;
        if (q.placeholder) { ta.placeholder = q.placeholder; }
        ta.addEventListener('input', function () { showError(''); syncDraft(q.name, ta.value); persistDraft(); });
        wrap.appendChild(ta);
      } else if (q.type === 'number') {
        /* 数字事实（如存款月数）：精确输入——档位区间会把「3 个月」和「6 个月」压成一档，
           行动账算不准；min/max 由 facts 声明给 */
        var num = doc.createElement('input');
        num.type = 'number';
        num.id = 'dz-' + q.name;
        num.className = 'tool-input dz-input';
        num.inputMode = 'numeric';
        if (q.min != null) { num.min = String(q.min); }
        if (q.max != null) { num.max = String(q.max); }
        if (q.placeholder) { num.placeholder = q.placeholder; }
        num.addEventListener('input', function () { showError(''); syncDraft(q.name, num.value); persistDraft(); });
        wrap.appendChild(num);
      } else {
        var sel = doc.createElement('select');
        sel.id = 'dz-' + q.name;
        sel.className = 'tool-select dz-input';
        var ph = doc.createElement('option');
        ph.value = '';
        ph.textContent = '请选择';
        sel.appendChild(ph);
        for (var i = 0; i < q.options.length; i++) {
          var opt = doc.createElement('option');
          opt.value = q.options[i];
          opt.textContent = q.options[i];
          sel.appendChild(opt);
        }
        sel.addEventListener('change', function () { showError(''); syncDraft(q.name, sel.value); persistDraft(); });
        wrap.appendChild(sel);
      }
      return wrap;
    }

    /* 步进问卷（照工具站 resignation-decision 打磨定稿的 quiz 交互，2026-09-23 迁移）：
       单题视图 + 选项 button 点选立即前进 + 右上 {n}/{total} + 脚部圆点分段进度条。
       圆点可点跳题、兼作唯一的回改入口——「上一题」按钮是老版打磨中刻意去掉的，不加回来。
       最后一格自由文本输入且必答全满时自动生成（生成内含平滑滚动到结果）。 */
    function renderStepper() {
      var qs = cfg.quiz;
      var ui = cfg.quizUI || {};
      var idx = 0;

      var head = el('div', 'dz-steps-head');
      var title = el('p', 'dz-steps-title');
      title.textContent = ui.title || '';
      head.appendChild(title);
      var prog = el('span', 'dz-steps-prog');
      head.appendChild(prog);

      var note = el('p', 'dz-steps-note');
      note.textContent = ui.note || '';

      var body = el('div', 'dz-steps-opts');
      var foot = el('div', 'dz-steps-foot');
      var dots = el('div', 'dz-dots');
      foot.appendChild(dots);

      /* AI 上下文桥（照老版 qd-quiz-ctx）：选项是 button，decide-ai 的控件扫描抓不到；
         hidden input 由 go() 同步「题干：选项原文」，label[for] 供扫描当字段名 */
      var ctx = doc.createElement('input');
      ctx.type = 'hidden';
      ctx.id = 'dz-quiz-ctx';
      var ctxLabel = doc.createElement('label');
      ctxLabel.setAttribute('for', 'dz-quiz-ctx');
      ctxLabel.hidden = true;
      ctxLabel.textContent = '问卷快照';

      quizBox.appendChild(head);
      quizBox.appendChild(note);
      quizBox.appendChild(body);
      quizBox.appendChild(foot);
      quizBox.appendChild(ctx);
      quizBox.appendChild(ctxLabel);

      /* requiredDone 提升到 mountScene 层（facts 必填也计入「答满」，见下方实现） */
      function autoRun() {
        var btn = doc.querySelector('[data-dz-generate]');
        if (btn) { btn.click(); }
      }
      function syncCtx() {
        var parts = [];
        for (var i = 0; i < qs.length; i++) {
          var v = String(picked[qs[i].name] || '');
          if (v === '') { continue; }
          parts.push(qs[i].label + '：' + v);
        }
        ctx.value = parts.join('；');
      }

      function stepNode() {
        var q = qs[idx];
        var box = el('div', 'dz-step');
        var cap = el('span', 'dz-label');
        cap.id = 'dz-cap-' + q.name;
        cap.appendChild(doc.createTextNode(q.label));
        if (q.optional) {
          var op = el('span', 'dz-label-opt');
          op.textContent = '选填';
          cap.appendChild(op);
        }
        box.appendChild(cap);
        if (q.hint) {
          var hint = el('span', 'dz-hint');
          hint.textContent = q.hint;
          box.appendChild(hint);
        }
        if (q.type === 'textarea') {
          var ta = doc.createElement('textarea');
          ta.id = 'dz-' + q.name;
          ta.className = 'tool-input dz-input';
          ta.rows = 3;
          ta.maxLength = 200;
          if (q.placeholder) { ta.placeholder = q.placeholder; }
          ta.value = picked[q.name] || '';
          ta.addEventListener('input', function () {
            picked[q.name] = ta.value;
            syncDraft(q.name, ta.value);
            persistDraft();
            showError('');
            syncCtx();
            /* 照老版「答满即算」：自由文本非空 + 必答全满 → 自动生成（不必找按钮） */
            if (ta.value.trim() !== '' && requiredDone()) { autoRun(); }
          });
          box.appendChild(ta);
        } else {
          var group = el('div', 'dz-opts');
          group.setAttribute('role', 'radiogroup');
          group.setAttribute('aria-labelledby', cap.id);
          for (var k = 0; k < q.options.length; k++) {
            (function (val) {
              var ob = el('button', 'dz-opt' + (picked[q.name] === val ? ' is-pick' : ''));
              ob.type = 'button';
              ob.setAttribute('aria-pressed', picked[q.name] === val ? 'true' : 'false');
              ob.textContent = val;
              ob.addEventListener('click', function () {
                picked[q.name] = val;
                syncDraft(q.name, val);
                persistDraft();
                showError('');
                /* 点选立即前进（老版无延迟）；回看屏由重渲染带出 is-pick */
                if (idx < qs.length - 1) { go(idx + 1); }
                else { step(); syncCtx(); if (requiredDone()) { autoRun(); } }
              });
              group.appendChild(ob);
            })(q.options[k]);
          }
          box.appendChild(group);
        }
        return box;
      }

      function step() {
        body.textContent = '';
        body.appendChild(stepNode());
      }

      function go(i) {
        idx = Math.max(0, Math.min(qs.length - 1, i));
        prog.textContent = (idx + 1) + ' / ' + qs.length;
        step();
        /* 圆点分段进度条（照老版打磨：未答 brand-2 / 已答 brand-4 / 当前 brand-6 三档
           同色系递进；点段跳题，兼作唯一回改入口） */
        dots.textContent = '';
        for (var j = 0; j < qs.length; j++) {
          (function (j) {
            var done = String(picked[qs[j].name] || '') !== '';
            var d = el('button', 'dz-dot' + (j === idx ? ' is-cur' : '') + (done ? ' is-done' : ''));
            d.type = 'button';
            d.setAttribute('aria-label', '第' + (j + 1) + '题');
            d.addEventListener('click', function () { go(j); });
            dots.appendChild(d);
          })(j);
        }
        syncCtx();
      }
      stepperGo = go;
      go(0);

      /* 「试试」示例（承工具站 examples 口径，2026-09-23 迁移）：⚡ 试试 + 典型处境 chips，
         点了一键回填并自动出结果——先看别人的一页长什么样，比自己从零填门槛低。
         样式直接复用 tools.css 的 .tool-examples/.tool-example（与工具站同源）。
         位置（2026-09-25 与 paper 一并对齐工具站主流口径）：优先挂模板的独立容器
         data-dz-examples（输入区末尾、生成按钮之前）——原挂 quizBox 末尾会插在
         问卷与事实表单之间，割裂两段式输入流；无容器回落旧位置（旧模板零破坏）。
         挂载前先清空容器：renderQuiz 重入不重复累积 chips。 */
      if (cfg.examples && cfg.examples.length) {
        var exSlot = root.querySelector('[data-dz-examples]');
        var exWrap = el('div', 'tool-examples');
        var exLabel = el('span', 'tool-examples-label');
        exLabel.appendChild(iconEl('zap'));
        exLabel.appendChild(doc.createTextNode('试试'));
        exWrap.appendChild(exLabel);
        for (var e = 0; e < cfg.examples.length; e++) {
          (function (ex) {
            var chip = el('button', 'tool-example');
            chip.type = 'button';
            chip.textContent = ex.label;
            chip.addEventListener('click', function () {
              setAnswers(ex.answers);
              showError('');
              if (requiredDone()) { autoRun(); }
            });
            exWrap.appendChild(chip);
          })(cfg.examples[e]);
        }
        if (exSlot) { exSlot.textContent = ''; exSlot.appendChild(exWrap); exSlot.hidden = false; }
        else { quizBox.appendChild(exWrap); }
      }
    }

    function renderQuiz() {
      quizBox.textContent = '';
      if (picked) { renderStepper(); }
      else {
        var grid = el('div', 'dz-grid');
        for (var i = 0; i < cfg.quiz.length; i++) {
          grid.appendChild(fieldOf(cfg.quiz[i]));
        }
        quizBox.appendChild(grid);
      }
      quizBox.hidden = false;
    }

    /* 事实字段区（B 方案两段式，2026-09-23）：感受题进步进问卷，事实走表单——
       数据本质决定交互形态，打分职责归 compute。cfg.facts 未配置或页面无容器时
       整段不渲染（旧场景页向后兼容） */
    function renderFacts() {
      if (!factsBox || !cfg.facts || !cfg.facts.length) { return; }
      factsBox.textContent = '';
      var ui = cfg.factsUI || {};
      if (ui.title) {
        var ft = el('p', 'dz-facts-title');
        ft.textContent = ui.title;
        factsBox.appendChild(ft);
      }
      if (ui.note) {
        var fn = el('p', 'dz-facts-note');
        fn.textContent = ui.note;
        factsBox.appendChild(fn);
      }
      var grid = el('div', 'dz-grid');
      for (var i = 0; i < cfg.facts.length; i++) {
        grid.appendChild(fieldOf(cfg.facts[i]));
      }
      factsBox.appendChild(grid);
      factsBox.hidden = false;
    }

    function answers() {
      var a = {};
      if (picked) {
        for (var k in picked) {
          if (Object.prototype.hasOwnProperty.call(picked, k)) { a[k] = picked[k]; }
        }
      } else {
        var inputs = quizBox.querySelectorAll('select, textarea, input[type="number"]');
        for (var i = 0; i < inputs.length; i++) {
          var name = String(inputs[i].id || '').replace(/^dz-/, '');
          if (name) { a[name] = String(inputs[i].value || '').trim(); }
        }
      }
      /* facts 表单值并入（number/select/textarea 同口径） */
      if (factsBox) {
        var fEls = factsBox.querySelectorAll('input, select, textarea');
        for (var i2 = 0; i2 < fEls.length; i2++) {
          var name2 = String(fEls[i2].id || '').replace(/^dz-/, '');
          if (name2) { a[name2] = String(fEls[i2].value || '').trim(); }
        }
      }
      return a;
    }

    function setAnswers(a) {
      if (picked) {
        for (var i = 0; i < cfg.quiz.length; i++) {
          var name = cfg.quiz[i].name;
          if (a[name] !== undefined && String(a[name]) !== '') { picked[name] = a[name]; }
        }
        /* 恢复后落在第一个未答的必答题上，而不是把人扔回第 1 题 */
        var at = 0;
        for (var j = 0; j < cfg.quiz.length; j++) {
          var qj = cfg.quiz[j];
          if (!qj.optional && String(picked[qj.name] || '') === '') { at = j; break; }
        }
        if (stepperGo) { stepperGo(at); }
        fillFacts(a);
        return;
      }
      var inputs = quizBox.querySelectorAll('select, textarea, input[type="number"]');
      for (var i2 = 0; i2 < inputs.length; i2++) {
        var name2 = String(inputs[i2].id || '').replace(/^dz-/, '');
        if (name2 && a[name2]) { inputs[i2].value = a[name2]; }
      }
      fillFacts(a);
    }

    /* 必答收口（老版「答满即算」的触发条件）：quiz 必答题（步进读 picked、平铺读 DOM）
       + facts 必填项全满才 true——quiz 答满但 facts 未填时不自动生成，等人填完 */
    function requiredDone() {
      var i;
      if (picked) {
        for (i = 0; i < cfg.quiz.length; i++) {
          var q = cfg.quiz[i];
          if (!q.optional && String(picked[q.name] || '') === '') { return false; }
        }
      } else {
        var qEls = quizBox.querySelectorAll('select, textarea, input[type="number"]');
        for (i = 0; i < qEls.length; i++) {
          if (String(qEls[i].value || '').trim() === '') { return false; }
        }
      }
      if (factsBox && cfg.facts) {
        var fEls = factsBox.querySelectorAll('input, select, textarea');
        for (i = 0; i < fEls.length; i++) {
          var fName = String(fEls[i].id || '').replace(/^dz-/, '');
          var fDef = null;
          for (var j = 0; j < cfg.facts.length; j++) {
            if (cfg.facts[j].name === fName) { fDef = cfg.facts[j]; break; }
          }
          if (fDef && !fDef.optional && String(fEls[i].value || '').trim() === '') { return false; }
        }
      }
      return true;
    }
    /* facts DOM 回填（断点恢复 / 示例回填共用） */
    function fillFacts(a) {
      if (!factsBox) { return; }
      var fEls = factsBox.querySelectorAll('input, select, textarea');
      for (var i = 0; i < fEls.length; i++) {
        var name = String(fEls[i].id || '').replace(/^dz-/, '');
        if (name && a[name] !== undefined && a[name] !== null) { fEls[i].value = a[name]; }
      }
    }

    /* 存档（不上传，隐私模式写失败静默跳过）。v2 结构（2026-09-24 回访闭环）：
       { answers, checkedActs, result, today }——生成时四件套随存，回访 restore
       直接渲染昨天的单子（纸面填写日期用生成日 lastToday，决定日已算死在 result）；
       中途点选/输入走 persistDraft 防抖只刷 answers——改了答案旧结果即作废
       （存档无 result 字段），避免「半新答案配旧结果」的误导；
       勾选/生成走 persistSnapshot 全量存。读取兼容 v1 纯 answers 平铺格式。 */
    function persistSnapshot() {
      try {
        global.localStorage.setItem(storeKey, JSON.stringify({
          answers: answers(),
          checkedActs: checkedActs,
          result: lastResult || null,
          today: lastResult ? lastToday : null
        }));
      } catch (e) { /* 忽略 */ }
    }
    var persistTimer = null;
    function persistDraft() {
      if (persistTimer) { global.clearTimeout(persistTimer); }
      persistTimer = global.setTimeout(function () {
        persistTimer = null;
        try { global.localStorage.setItem(storeKey, JSON.stringify({ answers: answers() })); } catch (e) { /* 忽略 */ }
      }, 400);
    }
    function restore() {
      try {
        var raw = global.localStorage.getItem(storeKey);
        if (!raw) { return; }
        var a = JSON.parse(raw);
        if (!a || typeof a !== 'object') { return; }
        if (a.answers && typeof a.answers === 'object') {
          /* v2：答案回填 + 勾选记忆 + 上次结果直接渲染——回访不用手点重新生成 */
          setAnswers(a.answers);
          if (a.checkedActs && typeof a.checkedActs === 'object') { checkedActs = a.checkedActs; }
          if (a.result && a.result.dims && a.today) {
            lastResult = a.result;
            lastToday = a.today;
            renderResult(a.result, a.today);
            /* DZ_LAST 同步重建：回访先聊 AI 也有结果上下文（口径与生成时一致）；
               不自动滚动——加载即跳走太突兀，结果区就在问卷下方 */
            global.DZ_LAST = { slug: slug, stay: a.result.stay, leave: a.result.leave, diff: a.result.diff, dims: a.result.dims, answers: a.answers, labels: labels };
            if (global.DZ_AI && typeof global.DZ_AI.refreshIntro === 'function') { global.DZ_AI.refreshIntro(); }
          }
        } else {
          setAnswers(a); /* v1 兼容：旧纯 answers 平铺格式 */
        }
      } catch (e) { /* 坏数据当没有 */ }
    }

    /* 结果渲染（全 textContent 写入）。单层纸面（2026-09-23 用户拍板：屏幕卡与
       打印纸两份内容重复，合并为一张可勾选的纸）：.tool-paper 即唯一结果层，
       填单日期/决定日/你填的情况/总分/三态解读全部进纸面，屏幕所见 = 打印成品；
       行动清单在纸面内直接可勾选（屏幕真 checkbox，打印换 ☑/□ 打卡盒）；
       操作按钮与脚注仍在纸外（打印摘除）。 */
    var lastResult = null;
    var lastToday = '';

    function gapText(n) { return (n > 0 ? '+' : '') + String(n); }

    function kvCell(k, v) {
      var cell = el('div', '');
      var kk = el('span', 'tp-k');
      kk.textContent = k;
      cell.appendChild(kk);
      cell.appendChild(doc.createTextNode(' '));
      var vv = el('span', 'tp-v');
      vv.textContent = v;
      cell.appendChild(vv);
      return cell;
    }

    /* —— 纸面 = 唯一结果层（老版 renderPaper 五段为基座，屏幕卡独有信息融入：
       决定日进 kv、summary「你填的情况」与三态解读成段，行动清单纸内可勾选）—— */
    function paperNode(r, today) {
      var box = el('div', 'tool-paper');

      /* ① 标题 ② kv：填单日期（原两层各一份，统一进纸面）+ 决定日 + 总分 */
      var title = el('p', 'tp-title');
      title.textContent = cfg.title;
      box.appendChild(title);
      var kv = el('div', 'tp-kv');
      kv.appendChild(kvCell('填单日期', today));
      kv.appendChild(kvCell('决定日', r.deadline + '（' + cfg.deadlineDays + ' 天后）'));
      kv.appendChild(kvCell('总分', stayShort + ' ' + r.stay + ' · ' + goShort + ' ' + r.leave + ' · 差 ' + gapText(r.diff)));
      box.appendChild(kv);

      /* ③ 你填的情况（原屏幕卡 summary 融入：复述输入，用户 2026-09-23 点名进纸面） */
      var sum = el('p', 'tp-summary');
      sum.textContent = r.summary;
      box.appendChild(sum);

      /* ④ 打分表（老版 renderPaper 1:1 四列「维度|{stay}|{go}|差」：tp-table + 数字右对齐 is-num） */
      var wrap = el('div', 'tp-table-wrap');
      var tbl = el('table', 'tp-table');
      var thead = el('thead', '');
      var hr = el('tr', '');
      var dh = ['维度', stayName, goName, '差'];
      for (var hi = 0; hi < dh.length; hi++) {
        var hth = el('th', hi > 0 ? 'is-num' : '');
        hth.textContent = dh[hi];
        hr.appendChild(hth);
      }
      thead.appendChild(hr);
      tbl.appendChild(thead);
      var tb = el('tbody', '');
      for (var di = 0; di < r.dims.length; di++) {
        var d = r.dims[di];
        var drow = el('tr', '');
        var dtd1 = el('td', '');
        dtd1.textContent = d.label;
        drow.appendChild(dtd1);
        var dtds = [String(d.stay), String(d.go), gapText(d.gap)];
        for (var ti = 0; ti < dtds.length; ti++) {
          var dtd = el('td', 'is-num');
          dtd.textContent = dtds[ti];
          drow.appendChild(dtd);
        }
        tb.appendChild(drow);
      }
      tbl.appendChild(tb);
      wrap.appendChild(tbl);
      box.appendChild(wrap);

      /* ⑤ 三态解读（原屏幕卡 note 融入：先逐维分，再一句话解读分差去向） */
      var read = el('p', 'tp-read');
      read.textContent = r.note;
      box.appendChild(read);

      /* ⑥ 行动清单表（老版：表头=actionsTitle「无论怎么选，先做这些」，
         行首 ☑/□ 放大打卡盒 qd-paper-box——样式 tools.css 2462 行） */
      var wrap2 = el('div', 'tp-table-wrap');
      var tbl2 = el('table', 'tp-table');
      var thead2 = el('thead', '');
      var hr2 = el('tr', '');
      var h3 = el('th', '');
      h3.textContent = '无论怎么选，先做这些';
      hr2.appendChild(h3);
      thead2.appendChild(hr2);
      tbl2.appendChild(thead2);
      var tb2 = el('tbody', '');
      for (var i = 0; i < r.actions.length; i++) {
        (function (act) {
          var text = act.text;
          var arow = el('tr', '');
          var atd = el('td', '');
          /* 行内真 checkbox（label 隐式关联，热区=整行文字）；qd-paper-box 字符盒
             屏幕隐藏、打印显示——勾选即时同步进盒，打印即所见（替代旧 syncPaper 整纸重建） */
          var lab = el('label', 'tp-act');
          var cb = doc.createElement('input');
          cb.type = 'checkbox';
          /* 勾选记忆（承老版 checkedActs）：重算后同文本的行动恢复勾选 */
          cb.checked = !!checkedActs[text];
          var mark = el('span', 'qd-paper-box');
          mark.textContent = cb.checked ? '☑' : '□';
          var txt = el('span', 'tp-act-text');
          txt.textContent = text;
          cb.addEventListener('change', function () {
            checkedActs[text] = cb.checked;
            var want = cb.checked ? '☑' : '□';
            if (mark.textContent !== want) { mark.textContent = want; }   /* 同状态零写入 */
            persistSnapshot();
          });
          lab.appendChild(cb);
          lab.appendChild(mark);
          lab.appendChild(txt);
          atd.appendChild(lab);
          /* cross（2026-09-25「行动条目内嵌工具」）：行动规则可配 {href,label}，
             渲染成条目尾部的行内弱化链接——出现在用户拿到清单、正想知道「这笔账
             怎么算」的时刻。必须放 label 外（放内点链接会切换 checkbox）；
             屏幕显示、打印摘除（decide.css 双保险），纸面保持干净 */
          if (act.cross && act.cross.href && act.cross.label) {
            var cross = doc.createElement('a');
            cross.className = 'tp-act-cross';
            cross.href = act.cross.href;
            cross.target = '_blank';
            cross.rel = 'noopener';
            cross.textContent = act.cross.label + ' →';
            atd.appendChild(cross);
          }
          arow.appendChild(atd);
          tb2.appendChild(arow);
        })(r.actions[i]);
      }
      tbl2.appendChild(tb2);
      wrap2.appendChild(tbl2);
      box.appendChild(wrap2);

      /* ⑦ note（老版 paperNote：免责，tp-note 灰底小字）。文案场景可配：
         「不构成职业建议」只贴辞职场景，修换/兴趣班等生活决策走各自措辞 */
      var note = el('p', 'tp-note');
      note.textContent = cfg.disclaimer || '本平衡单只是帮你梳理，不构成职业建议。情绪持续低落或影响睡眠时，请优先照顾自己（全国心理援助热线 12356）。';
      box.appendChild(note);

      return box;
    }

    function printBtn() {
      var b = el('button', 'tool-btn tool-btn--primary');
      b.type = 'button';
      b.setAttribute('data-dz-print', '');
      b.appendChild(iconEl('file'));
      b.appendChild(doc.createTextNode('打印平衡单'));
      b.addEventListener('click', doPrint);
      return b;
    }

    function againBtn() {
      var b = el('button', 'tool-btn');
      b.type = 'button';
      b.setAttribute('data-dz-regenerate', '');
      b.appendChild(iconEl('rotate-cw'));
      b.appendChild(doc.createTextNode('重填一次'));
      b.addEventListener('click', function () {
        try { global.localStorage.removeItem(storeKey); } catch (e) { /* 忽略 */ }
        if (persistTimer) { global.clearTimeout(persistTimer); persistTimer = null; }
        lastResult = null;
        lastToday = '';
        if (picked) {
          for (var k in picked) {
            if (Object.prototype.hasOwnProperty.call(picked, k)) { delete picked[k]; }
          }
          if (stepperGo) { stepperGo(0); }
        }
        /* 草稿与判分快照同步清空：重填后先聊，AI 不该拿到旧答案/旧结果——
           collectContext 优先读 DZ_LAST，不清它则新填的 DZ_DRAFT 被优先级屏蔽 */
        if (global.DZ_DRAFT) { global.DZ_DRAFT.answers = {}; }
        global.DZ_LAST = null;
        var inputs = quizBox.querySelectorAll('select, textarea');
        for (var i = 0; i < inputs.length; i++) { inputs[i].value = ''; }
        if (resultBox) {
          resultBox.hidden = true;
          resultBox.textContent = '';
        }
        showError('');
        try { root.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) { /* 老浏览器 */ }
      });
      return b;
    }

    /* 结果渲染（生成与回访恢复共用）：纸面即唯一结果层，操作按钮与脚注在
       纸外（打印时整块摘除，只印纸面）；lastResult/lastToday 在此统一落值 */
    function renderResult(r, today) {
      if (!resultBox) { return; }
      resultBox.textContent = '';
      lastResult = r;
      lastToday = today;
      resultBox.appendChild(paperNode(r, today));
      var acts = el('div', 'dz-result-actions');
      acts.appendChild(printBtn());
      acts.appendChild(againBtn());
      resultBox.appendChild(acts);
      var foot = el('p', 'dz-result-foot');
      foot.textContent = '这一页只存在于你自己的浏览器里；打印出来贴在桌前，AI 疏导在下方。';
      resultBox.appendChild(foot);
      resultBox.hidden = false;
    }

    /* 打印：body 挂 dz-printing，print CSS 只印 .tool-paper 纸面（display 链摘掉
       纸外元素，行动清单 checkbox 换 ☑/□ 打卡盒）；afterprint 摘掉 + 1s 兜底（部分浏览器不触发 afterprint） */
    function doPrint() {
      if (!resultBox || resultBox.hidden) { return; }
      doc.body.classList.add('dz-printing');
      var cleanup = function () {
        doc.body.classList.remove('dz-printing');
        global.removeEventListener('afterprint', cleanup);
      };
      global.addEventListener('afterprint', cleanup);
      global.setTimeout(cleanup, 1000);
      global.print();
    }

    /* 生成：校验 → 判分 → 渲染 */
    genBtn.addEventListener('click', function () {
      var a = answers();
      var missing = 0;
      for (var i = 0; i < cfg.quiz.length; i++) {
        var q = cfg.quiz[i];
        if (!q.optional && String(a[q.name] || '') === '') { missing++; }
      }
      if (cfg.facts) {
        for (var i2 = 0; i2 < cfg.facts.length; i2++) {
          var f = cfg.facts[i2];
          if (!f.optional && String(a[f.name] || '') === '') { missing++; }
        }
      }
      if (missing > 0) {
        showError('还有 ' + missing + ' 项没填——都是 30 秒就能答完的，答案越全，清单越贴你的处境。');
        return;
      }
      var r = cfg.compute(a, todayISO());
      if (!r) { showError('判分出错了，请重试一次'); return; }
      showError('');
      renderResult(r, todayISO());
      try { resultBox.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) { /* 老浏览器 */ }
      persistSnapshot();
      /* 判分快照给 AI 疏导（「AI 看得见结果」的决策站落点）；步进模式下 DOM 只有
         当前题，全量答案 + 题名映射随快照一起挂上，decide-ai 收集器优先读它；
         dims/diff 一并随快照（收集器取 |gap| 前 2 维拼「分差主要来自…」），
         生成后顺手刷新 AI 引导语（refreshIntro 把分差主要维度带进入口文案） */
      global.DZ_LAST = { slug: slug, stay: r.stay, leave: r.leave, diff: r.diff, dims: r.dims, answers: a, labels: labels };
      if (global.DZ_AI && typeof global.DZ_AI.refreshIntro === 'function') { global.DZ_AI.refreshIntro(); }
    });

    /* AI 首问示例按场景切换（cfg.aiHint）：模板兜底是中性文案，场景数据就位后
       换成「像这个场景里的人会说的话」——示例贴场景，用户才知道往哪个方向说；
       cfg.aiHint 缺省时保留模板兜底（旧场景数据零破坏） */
    var aiText = doc.querySelector('[data-feedback-text]');
    if (aiText && cfg.aiHint) { aiText.setAttribute('placeholder', cfg.aiHint); }

    renderQuiz();
    renderFacts();
    restore();
  }

  function boot() {
    initTheme();
    initBackTop();
    var roots = doc.querySelectorAll('[data-dz-scene]');
    for (var i = 0; i < roots.length; i++) { mountScene(roots[i]); }
  }

  /* boot 时机：defer 脚本按文档顺序执行——若本文件先于场景 data js 执行，
     DZ_SCENES 还是空对象，同步 boot 会让 mountScene 因 !cfg 静默 return（问卷永不渲染、零报错）。
     因此 deferred 场景下一律等 DOMContentLoaded（所有 defer 执行完才触发）；仅动态注入
     （readyState 已 complete）才立即跑。
     无 document（Node 冒烟 loadCfg 只为取 DZ_NOTE3 在 sandbox 里执行本文件）→ 不 boot 静默退出。 */
  if (!doc) {
    /* Node sandbox：DZ_UI/DZ_NOTE3 已挂到 window，到此为止 */
  } else if (doc.readyState === 'complete') {
    boot();
  } else {
    doc.addEventListener('DOMContentLoaded', boot);
  }
})(typeof window !== 'undefined' ? window : globalThis);
